import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import NipponBotInteractionsTable from './NipponBotInteractionsTable';
import ResumePreviewDialog from './ResumePreviewDialog';

const STATUS_COLOR = {
  Completed: 'success',
  completed: 'success',
  partial: 'warning',
  failed: 'error',
  Failed: 'error',
  processing: 'info',
  Processing: 'info'
};

const DOB_DISPLAY_FORMAT = 'DD/MM/YYYY';
const EMPTY = '—';

const formatDateTime = (value) => {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? EMPTY : date.toLocaleString();
};

const getInitials = (name = '') => {
  const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join('');
  return initials || '?';
};

const createEditForm = (candidate) => ({
  fullName: candidate?.fullName || '',
  email: candidate?.email || '',
  mobile: candidate?.mobile || '',
  dob: candidate?.dob || '',
  currentEmployer: candidate?.currentEmployer || '',
  currentRole: candidate?.currentRole || '',
  totalExperience: candidate?.totalExperience || '',
  location: candidate?.location || '',
  skills: Array.isArray(candidate?.skills) ? candidate.skills.join(', ') : '',
  sapCode: candidate?.sapCode || '',
  branchManagerId: candidate?.branchManagerId || '',
  branchManagerRef: candidate?.branchManagerRef || ''
});

const getCandidateRowsFromListResponse = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getCandidateFromDetailResponse = (response) => {
  const payload = response?.data?.data;
  return payload?.data || payload || null;
};

const FieldGroup = ({ title, children }) => (
  <Box sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.6 }}>
      {title}
    </Typography>
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
);

const ReadField = ({ label, value }) => (
  <Typography variant="body2" sx={{ mb: 0.75 }}><strong>{label}:</strong> {value || EMPTY}</Typography>
);

const formatInterviewStatus = (value = '') => {
  if (!value) return 'Unknown';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const renderComparisonSummary = (summary) => {
  if (!summary) return null;
  if (typeof summary === 'string') return summary;
  if (Array.isArray(summary)) return summary.filter(Boolean).join(', ');
  if (typeof summary === 'object') {
    const parts = [
      summary.reason,
      summary.overallStatus ? `Overall: ${formatInterviewStatus(summary.overallStatus)}` : '',
      Number.isFinite(summary.yesCount) ? `Yes: ${summary.yesCount}` : '',
      Number.isFinite(summary.noCount) ? `No: ${summary.noCount}` : '',
      Number.isFinite(summary.naCount) ? `N/A: ${summary.naCount}` : ''
    ].filter(Boolean);
    return parts.join(' | ');
  }
  return String(summary);
};

export default function CandidateDetailPage({ onMessage }) {
  const { candidateUuid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = localStorage.getItem('token');
  let decodedToken = {};
  try {
    decodedToken = token ? JSON.parse(atob(token.split('.')[1])) : {};
  } catch {
    decodedToken = {};
  }
  const isBranchManager = decodedToken?.userRole === 'BranchManager';
  const isCurator = decodedToken?.userRole === 'Curator';
  const listEndpoint = isBranchManager ? nipponBotApiEndpoints.branchManagerCandidateList : nipponBotApiEndpoints.candidateList;
  const detailEndpoint = isBranchManager ? nipponBotApiEndpoints.branchManagerCandidateDetail : nipponBotApiEndpoints.candidateProfileDetail;
  const updateEndpoint = isBranchManager ? nipponBotApiEndpoints.branchManagerCandidateUpdate : nipponBotApiEndpoints.candidateUpdate;
  const resumeUploadEndpoint = isBranchManager ? nipponBotApiEndpoints.branchManagerResumeUpload : nipponBotApiEndpoints.resumeUpload;
  const backPath = isBranchManager ? '/user/bot-interviews/branch-candidates' : '/user/bot-interviews/candidates';
  const [loading, setLoading] = useState(true);
  const [detailCandidate, setDetailCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(searchParams.get('edit') === '1');
  const [editForm, setEditForm] = useState(createEditForm(null));
  const [saving, setSaving] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [previewResume, setPreviewResume] = useState(null);
  const [branchManagers, setBranchManagers] = useState([]);
  const resumeInputRef = useRef(null);

  const loadBranchManagers = async () => {
    if (!isCurator) return;
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.branchManagerAccountList, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = response?.data?.data;
      const accounts = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      setBranchManagers(accounts.filter((account) => account.status !== 'inactive'));
    } catch (error) {
      onMessage?.(error?.response?.data?.status?.value || error?.response?.data?.message || 'Failed to load Branch Managers.', 'error');
    }
  };

  const loadCandidate = async () => {
    setLoading(true);
    try {
      const listResponse = await axiosInstance.get(listEndpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params: { searchTerm: candidateUuid, limit: 50 }
      });
      let candidates = getCandidateRowsFromListResponse(listResponse);
      let matchedCandidate = candidates.find(
        (candidate) => candidate.candidateUuid === candidateUuid || candidate.id === candidateUuid
      );

      if (!matchedCandidate?.id) {
        const fallbackResponse = await axiosInstance.get(listEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 1000 }
        });
        candidates = getCandidateRowsFromListResponse(fallbackResponse);
        matchedCandidate = candidates.find(
          (candidate) => candidate.candidateUuid === candidateUuid || candidate.id === candidateUuid
        );
      }

      if (!matchedCandidate?.id) {
        setDetailCandidate(null);
        onMessage?.('Candidate not found.', 'error');
        return;
      }

      const detailResponse = await axiosInstance.get(`${detailEndpoint}/${matchedCandidate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const candidate = getCandidateFromDetailResponse(detailResponse);
      setDetailCandidate(candidate);
      setEditForm(createEditForm(candidate));
    } catch (error) {
      setDetailCandidate(null);
      onMessage?.(error?.response?.data?.message || 'Failed to load candidate details.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateUuid]);

  useEffect(() => {
    loadBranchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurator]);

  const handleEditChange = (field) => (event) => setEditForm((current) => ({ ...current, [field]: event.target.value }));
  const handleBranchManagerChange = (event) => {
    const branchManagerId = event.target.value;
    const selected = branchManagers.find((account) => account.id === branchManagerId);
    setEditForm((current) => ({
      ...current,
      branchManagerId,
      branchManagerRef: selected?.branchManagerRef || ''
    }));
  };

  const handleDobChange = (value) => {
    const formatted = value && value.isValid() ? value.format(DOB_DISPLAY_FORMAT) : '';
    setEditForm((current) => ({ ...current, dob: formatted }));
  };

  const handleCancelEdit = () => {
    setEditForm(createEditForm(detailCandidate));
    setEditMode(false);
    searchParams.delete('edit');
    setSearchParams(searchParams, { replace: true });
  };

  const handleSaveEdit = async () => {
    if (!editForm.fullName.trim()) return onMessage?.('Candidate full name is required.', 'error');

    setSaving(true);
    try {
      const payload = {
        ...editForm,
        branchManagerRef: undefined,
        skills: editForm.skills.split(',').map((item) => item.trim()).filter(Boolean)
      };
      await axiosInstance.put(`${updateEndpoint}/${detailCandidate.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onMessage?.('Candidate updated successfully.', 'success');
      setEditMode(false);
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
      await loadCandidate();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to update candidate.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleViewResume = (resume) => {
    if (!resume?.id) return;
    if (resume.fileAvailable === false) return;
    setPreviewResume(resume);
  };

  const chooseResumeFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !detailCandidate?.id) return;

    setUploadingResume(true);
    try {
      const resumeFormData = new FormData();
      resumeFormData.append('resume', file);
      resumeFormData.append('candidateId', detailCandidate.id);

      await axiosInstance.post(resumeUploadEndpoint, resumeFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      onMessage?.('Resume uploaded successfully.', 'success');
      await loadCandidate();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to upload resume.', 'error');
    } finally {
      setUploadingResume(false);
      if (resumeInputRef.current) resumeInputRef.current.value = '';
    }
  };

  const resumes = detailCandidate?.resumes || [];
  const assignedInterviews = detailCandidate?.assignedInterviews || [];
  const selectedBranchManager = branchManagers.find((account) => (
    (detailCandidate?.branchManagerRef && account.branchManagerRef === detailCandidate.branchManagerRef)
    || (editForm?.branchManagerRef && account.branchManagerRef === editForm.branchManagerRef)
    || (editForm?.branchManagerId && account.id === editForm.branchManagerId)
  ));

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      {loading ? (
        <Paper sx={{ p: 6, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Paper>
      ) : !detailCandidate ? (
        <Alert severity="info">Candidate details are not available for this link.</Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ width: 56, height: 56, fontSize: '1.3rem', fontWeight: 700, bgcolor: 'primary.main' }}>
                  {getInitials(detailCandidate.fullName)}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                    {detailCandidate.fullName || 'Unnamed Candidate'}
                  </Typography>
                  {detailCandidate.email ? (
                    <Typography variant="body2" color="text.secondary">{detailCandidate.email}</Typography>
                  ) : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={detailCandidate.resume ? `Resume: ${formatInterviewStatus(detailCandidate.resume.extractionStatus)}` : 'No resume on file'}
                      color={detailCandidate.resume ? (STATUS_COLOR[detailCandidate.resume.extractionStatus] || 'default') : 'default'}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Button variant="outlined" onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
                Back To Candidates
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)} sx={{ px: 2, borderBottom: '1px solid #eee' }}>
              <Tab value="profile" label="Profile" />
              <Tab value="interviews" label="Interviews" />
            </Tabs>

            <Box sx={{ p: 3, display: 'grid', gap: 3 }}>
              {activeTab === 'profile' ? (
                <>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Candidate Profile</Typography>
                      {!editMode ? (
                        <Button variant="outlined" size="small" onClick={() => setEditMode(true)} sx={{ textTransform: 'none' }}>
                          Edit
                        </Button>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          <Button variant="text" size="small" onClick={handleCancelEdit} disabled={saving} sx={{ textTransform: 'none' }}>
                            Cancel
                          </Button>
                          <Button variant="contained" size="small" onClick={handleSaveEdit} disabled={saving} sx={{ textTransform: 'none' }}>
                            {saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Save'}
                          </Button>
                        </Stack>
                      )}
                    </Stack>

                    {!editMode ? (
                      <>
                        <FieldGroup title="Contact">
                          <ReadField label="Full Name" value={detailCandidate.fullName} />
                          <ReadField label="Email" value={detailCandidate.email} />
                          <ReadField label="Date of Birth" value={detailCandidate.dob} />
                        </FieldGroup>
                        <FieldGroup title="Professional">
                          <ReadField label="Current Employer" value={detailCandidate.currentEmployer} />
                          <ReadField label="Current Role" value={detailCandidate.currentRole} />
                          <ReadField label="Total Experience" value={detailCandidate.totalExperience} />
                          <ReadField label="Location" value={detailCandidate.location} />
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Skills:</strong></Typography>
                            {Array.isArray(detailCandidate.skills) && detailCandidate.skills.length ? (
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {detailCandidate.skills.map((skill) => (
                                  <Chip key={skill} size="small" label={skill} variant="outlined" />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">{EMPTY}</Typography>
                            )}
                          </Box>
                        </FieldGroup>
                        <FieldGroup title="Internal">
                          <ReadField label="SAP Code" value={detailCandidate.sapCode} />
                          {isCurator ? (
                            <ReadField
                              label="Branch Manager"
                              value={selectedBranchManager
                                ? `${selectedBranchManager.firstName || ''} ${selectedBranchManager.lastName || ''}`.trim()
                                : ''}
                            />
                          ) : null}
                        </FieldGroup>
                      </>
                    ) : (
                      <>
                        <FieldGroup title="Contact">
                          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <TextField label="Full Name" value={editForm.fullName} onChange={handleEditChange('fullName')} fullWidth />
                            <TextField label="Email" value={editForm.email} onChange={handleEditChange('email')} fullWidth />
                            <TextField label="Mobile" value={editForm.mobile} onChange={handleEditChange('mobile')} fullWidth />
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                              <DatePicker
                                label="Date of Birth"
                                format={DOB_DISPLAY_FORMAT}
                                value={editForm.dob ? moment(editForm.dob, DOB_DISPLAY_FORMAT) : null}
                                onChange={handleDobChange}
                                disableFuture
                                slotProps={{ textField: { fullWidth: true } }}
                              />
                            </LocalizationProvider>
                          </Box>
                        </FieldGroup>
                        <FieldGroup title="Professional">
                          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <TextField label="Current Employer" value={editForm.currentEmployer} onChange={handleEditChange('currentEmployer')} fullWidth />
                            <TextField label="Current Role" value={editForm.currentRole} onChange={handleEditChange('currentRole')} fullWidth />
                            <TextField label="Total Experience" value={editForm.totalExperience} onChange={handleEditChange('totalExperience')} fullWidth />
                            <TextField label="Location" value={editForm.location} onChange={handleEditChange('location')} fullWidth />
                            <TextField
                              label="Skills"
                              placeholder="Comma-separated, e.g. Sales, Negotiation, CRM"
                              value={editForm.skills}
                              onChange={handleEditChange('skills')}
                              fullWidth
                              sx={{ gridColumn: { md: '1 / -1' } }}
                            />
                          </Box>
                        </FieldGroup>
                        <FieldGroup title="Internal">
                          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                            <TextField label="SAP Code" value={editForm.sapCode} onChange={handleEditChange('sapCode')} fullWidth />
                            {isCurator ? (
                              <FormControl fullWidth>
                                <InputLabel id="candidate-branch-manager-label">Branch Manager</InputLabel>
                                <Select
                                  labelId="candidate-branch-manager-label"
                                  label="Branch Manager"
                                  value={selectedBranchManager?.id || editForm.branchManagerId || ''}
                                  onChange={handleBranchManagerChange}
                                >
                                  <MenuItem value="">Unassigned</MenuItem>
                                  {branchManagers.map((account) => (
                                    <MenuItem key={account.id} value={account.id}>
                                      {`${account.firstName || ''} ${account.lastName || ''}`.trim() || account.userName || account.employeeId || 'Branch Manager'}
                                      {account.employeeId ? ` (${account.employeeId})` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : null}
                          </Box>
                        </FieldGroup>
                      </>
                    )}
                  </Box>

                  <Box sx={{ borderTop: '1px solid #eee', pt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Resume</Typography>
                      <Button variant="outlined" size="small" component="label" disabled={uploadingResume} sx={{ textTransform: 'none' }}>
                        {uploadingResume ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        Upload New Resume
                        <input ref={resumeInputRef} hidden type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={chooseResumeFile} />
                      </Button>
                    </Stack>
                    {!detailCandidate.resume ? (
                      <Alert severity="info">No extracted resume is linked to this candidate yet.</Alert>
                    ) : (
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" label={formatInterviewStatus(detailCandidate.resume.extractionStatus)} color={STATUS_COLOR[detailCandidate.resume.extractionStatus] || 'default'} />
                          <Typography variant="caption" color="text.secondary">Uploaded {formatDateTime(detailCandidate.resume.createdAt)}</Typography>
                          <Typography variant="caption" color="text.secondary">Extracted {formatDateTime(detailCandidate.resume.extractedAt)}</Typography>
                        </Stack>
                        <Typography variant="body2"><strong>File:</strong> {detailCandidate.resume.originalFileName || EMPTY}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Professional details above were auto-filled from this resume when they were empty - edit above to override.
                        </Typography>
                        {detailCandidate.resume.id ? (
                          <Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewResume(detailCandidate.resume)}
                              disabled={detailCandidate.resume.fileAvailable === false}
                              sx={{ textTransform: 'none' }}
                            >
                              {detailCandidate.resume.fileAvailable === false ? 'Resume not available' : 'View Resume'}
                            </Button>
                          </Box>
                        ) : null}
                      </Stack>
                    )}

                    {resumes.length > 1 ? (
                      <Box sx={{ mt: 2.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Earlier uploads</Typography>
                        <Stack spacing={1}>
                          {resumes.filter((resume) => resume.id !== detailCandidate.resume?.id).map((resume) => (
                            <Stack key={resume.id} direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ border: '1px solid #eee', borderRadius: 2, px: 1.5, py: 1 }}>
                              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                                <Typography variant="body2">{resume.originalFileName || EMPTY}</Typography>
                                <Chip size="small" label={formatInterviewStatus(resume.extractionStatus)} color={STATUS_COLOR[resume.extractionStatus] || 'default'} />
                                <Typography variant="caption" color="text.secondary">{formatDateTime(resume.createdAt)}</Typography>
                              </Stack>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() => handleViewResume(resume)}
                                disabled={resume.fileAvailable === false}
                                sx={{ textTransform: 'none' }}
                              >
                                {resume.fileAvailable === false ? 'Unavailable' : 'View'}
                              </Button>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    ) : null}
                  </Box>
                </>
              ) : isBranchManager ? (
                <Box sx={{ display: 'grid', gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Assigned Interviews</Typography>
                  {!assignedInterviews.length ? (
                    <Alert severity="info">No interviews are linked to this candidate yet.</Alert>
                  ) : (
                    <Stack spacing={1.5}>
                      {assignedInterviews.map((interview) => (
                        <Paper key={interview.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack spacing={1}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                {interview.originalFileName || 'Interview Recording'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDateTime(interview.recordingDateTime || interview.createdAt)}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip size="small" label={`Recording: ${interview.recordingId ? 'Available' : 'Missing'}`} />
                              <Chip size="small" label={`Transcription: ${interview.transcriptionId ? 'Available' : 'Pending'}`} />
                              <Chip size="small" label={`Interaction: ${interview.interactionId ? 'Available' : 'Pending'}`} />
                            </Stack>
                            {interview.resumeInterviewComparison ? (
                              <Box sx={{ pt: 0.5 }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Resume Match Status:</strong> {formatInterviewStatus(interview.resumeInterviewComparison.status)}
                                </Typography>
                                {interview.resumeInterviewComparison.summary ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {renderComparisonSummary(interview.resumeInterviewComparison.summary)}
                                  </Typography>
                                ) : null}
                              </Box>
                            ) : null}
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              ) : (
                <NipponBotInteractionsTable variant="default" candidateId={detailCandidate.id} />
              )}
            </Box>
          </Paper>
        </>
      )}

      <ResumePreviewDialog open={Boolean(previewResume)} resume={previewResume} onClose={() => setPreviewResume(null)} allowDownload={!isBranchManager} />
    </Box>
  );
}
